/**
 * ClientOnboardingForm Component
 * Main form page for client onboarding process
 * Renders the dynamic form based on the token's form schema
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTokenForm } from '../../hooks';
import { DynamicFormRenderer } from '../../components/forms';
import { LoadingSpinner, OnboardingProgressBar } from '../../components/ui';
import publicTokenService from '../../services/publicTokenService';
import styles from './ClientOnboardingForm.module.css';

const ClientOnboardingForm = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const {
        tokenData,
        loading,
        error,
        formData,
        isValid,
        submitForm,
        updateFields,
    } = useTokenForm(token);
    
    const [submitted, setSubmitted] = useState(false);
    const [submitResult, setSubmitResult] = useState(null);
    const [showSaveNotification, setShowSaveNotification] = useState(false);
    const [isTestMode, setIsTestMode] = useState(false);

    // Check for test mode
    useEffect(() => {
        const testMode = sessionStorage.getItem('onboarding_test_mode') === 'true';
        setIsTestMode(testMode);
    }, []);

    /**
     * Handle form submission
     */
    const handleSubmit = async (data) => {
        // In test mode, just show success without submitting
        if (isTestMode) {
            setSubmitResult({
                success: true,
                message: 'Test submission successful! (No data was actually saved)'
            });
            setSubmitted(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        try {
            const result = await submitForm(data);
            setSubmitResult(result);
            setSubmitted(true);
            
            if (result.success) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (err) {
            console.error('Failed to submit form:', err);
            setSubmitResult({
                success: false,
                message: 'Failed to submit form. Please try again.'
            });
        }
    };

    /**
     * Handle save draft
     */
    const handleSaveDraft = () => {
        // In a real implementation, this would save to backend
        setShowSaveNotification(true);
        setTimeout(() => setShowSaveNotification(false), 3000);
    };

    /**
     * Handle exit
     */
    const handleExit = () => {
        if (window.confirm('Are you sure you want to exit? Your progress will be saved and you can return using the same link.')) {
            navigate('/');
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.loadingContainer}>
                    <div className={styles.spinner}></div>
                    <p>Loading your onboarding form...</p>
                </div>
            </div>
        );
    }

    // Invalid token or missing form
    if (!isValid || error || !tokenData?.formSchema) {
        return (
            <div className={styles.container}>
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <h2>Form Not Available</h2>
                    {!tokenData?.formSchema && isValid ? (
                        <>
                            <p>
                                This onboarding link is valid, but no form is associated with it.
                            </p>
                            <p className={styles.helpText}>
                                Please contact support to receive a new onboarding link.
                            </p>
                        </>
                    ) : (
                        <>
                            <p>
                                {tokenData?.message || error || 'This onboarding link is no longer valid.'}
                            </p>
                            <p className={styles.helpText}>
                                Please contact support if you need a new onboarding link.
                            </p>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // Success state
    if (submitted && submitResult?.success) {
        return (
            <div className={styles.container}>
                <div className={styles.successContainer}>
                    <div className={styles.logoContainer}>
                        <img 
                            src="/Alt Logo Flat.png" 
                            alt="WETLAB" 
                            className={styles.logoSmall}
                        />
                    </div>
                    <div className={styles.successIcon}>✓</div>
                    <h2>Thank You!</h2>
                    <p className={styles.successMessage}>
                        Your onboarding information has been successfully submitted.
                    </p>
                    <p className={styles.nextSteps}>
                        Our team will review your information and be in touch shortly to complete your account setup.
                    </p>
                    <div className={styles.successActions}>
                        <button 
                            onClick={() => navigate('/')}
                            className={styles.homeButton}
                        >
                            Return to Home
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Form rendering
    const formTitle = tokenData.formSchema?.schema?.title || 
                     tokenData.formSchema?.title || 
                     'Client Onboarding';
    const formDescription = tokenData.formSchema?.schema?.description || 
                           tokenData.formSchema?.description;

    return (
        <React.Fragment>
            <OnboardingProgressBar />
            <div className={styles.container}>
                <div className={styles.header}>
                <div className={styles.logoHeader}>
                    <img 
                        src="/Alt Logo Flat.png" 
                        alt="WETLAB" 
                        className={styles.logoHeaderImg}
                    />
                </div>
                <button 
                    onClick={handleExit}
                    className={styles.exitButton}
                    title="Save and continue later"
                >
                    ✕ Save and Continue Later
                </button>
            </div>

            {showSaveNotification && (
                <div className={styles.saveNotification}>
                    <span className={styles.checkIcon}>✓</span>
                    Your progress has been saved
                </div>
            )}

            <div className={styles.formContainer}>
                <div className={styles.formHeader}>
                    <h1 className={styles.formTitle}>{formTitle}</h1>
                    {formDescription && (
                        <p className={styles.formDescription}>{formDescription}</p>
                    )}
                    
                    {tokenData?.expiresAt && (
                        <div className={styles.expiryBadge}>
                            <span className={styles.clockIcon}>🕒</span>
                            <span>Link expires {publicTokenService.formatExpirationTime(tokenData.expiresAt)}</span>
                        </div>
                    )}
                </div>

                <div className={styles.formContent}>
                    <DynamicFormRenderer
                        formSchema={tokenData?.formSchema}
                        prefillData={formData}
                        onSubmit={handleSubmit}
                        onCancel={() => navigate(`/client-onboarding/${token}`)}
                    />
                </div>

                {submitResult && !submitResult.success && (
                    <div className={styles.errorMessage}>
                        <span className={styles.errorIcon}>⚠️</span>
                        {submitResult.message}
                    </div>
                )}
            </div>

            <div className={styles.footer}>
                <p className={styles.footerText}>
                    Need assistance? Contact us at <a href="mailto:support@wetlab.com">support@wetlab.com</a>
                </p>
            </div>
        </div>
        </React.Fragment>
    );
};export default ClientOnboardingForm;
